/**
 * 「어느 캘린더를 보이나」 — 판정을 **한 곳에** 둔다 (2026-09-30).
 *
 * ## ⛔ 고치는 결함 둘 — 둘 다 운영에서 일어나고 있었다
 *
 * ```
 * ⓐ 캘린더 «하나»만 꺼도 「캘린더를 안 고른 일정」이 함께 사라진다
 *    서버는 calendarId: { in: [...] } 로 거른다. ⛔ SQL 의 IN 은 «NULL 을 뺀다».
 *    ⇒ calendarId = NULL 인 일정이 «통째로» 빠진다
 * ⓑ 캘린더를 «전부» 끄면 «전부 보인다» (정반대)
 *    [] 는 length > 0 이 거짓 ⇒ undefined ⇒ 서버가 전부 준다
 * ```
 *
 * ## ⭐ 그리고 ⓐ 는 «드문 경우»가 아니다 — 그것이 «기본»이다
 *
 * ```
 * EventCreateModal :393   <option value="">기본 캘린더</option>   ← 고르개의 «첫 값»
 *                  :224   ...(calendarId ? { calendarId } : {})   ← 빈 값이면 «안 보낸다»
 * calendar.routes  :272   let calendarId = null; …                ← 그대로 NULL 로 저장
 * ⇒ 사용자가 고르개를 안 건드리면 «모든 새 일정»이 calendarId = NULL 이다
 * ```
 *
 * ⚠ 그리고 그 통에는 **사이드바 체크박스가 없었다** — 끄면 되살릴 길이 없었다.
 *   그래서 [NO_CALENDAR] 를 «하나의 통»으로 세우고 체크박스를 준다.
 *
 * ## ⚠ 「기본 캘린더」라는 이름에 대하여 — 실측
 *
 * `calendars.isDefault` 칸은 **있지만 아무도 참으로 «세우지 않는다»**
 * (만드는 곳은 `calendars.routes.ts:103` 한 곳뿐이고 `isDefault: false` 로 박혀 있다.
 *  로컬 DB 실측 0/2). ⇒ **「기본 캘린더」라는 «행»은 없다.**
 * 고르개의 「기본 캘린더」는 사실 **「캘린더 없음」**이다.
 * ⇒ 그래도 사이드바에 **같은 이름**을 쓴다 — 사용자가 고르개에서 본 말과 이어져야
 *   「내가 그냥 만든 일정이 여기 있구나」를 알 수 있다. 이름을 서로 다르게 하면 못 잇는다.
 *
 * ## ⇒ 왜 «프론트만»으로 고치나 (재서 고른 값)
 *
 * ```
 * ✅ 일정 DTO 가 calendarId 를 이미 실어 온다 (calendar-helpers.ts:104 · api/calendar.ts:64)
 *    ⇒ 「NULL 통도 보인다」를 서버에 «말할 필요 없이» 화면이 가를 수 있다
 * ✅ 서버를 고치면 그 끝점을 쓰는 다른 호출자에 닿는다 — 전수로 세었다:
 *    listEvents 호출 여섯 중 «셋»(StatsCards · TodayEventsWidget · widget-queries)은
 *    calendarIds 를 «아예 안 보낸다» ⇒ 서버 고침이 그쪽엔 안 닿지만,
 * ⛔ 서버를 고치면 «서버+프론트 한 쌍»이 되어 배포를 맞춰야 한다(CLAUDE.md 규칙).
 *    이 결함은 그 값을 치를 만큼 크지 않다 — 화면이 혼자 바로잡을 수 있다
 * ⚠ 값: 숨긴 캘린더의 일정도 «전선을 건넌다». 범위가 한 달·한 주라 크기가 묶여 있다.
 *    나중에 무거워지면 서버가 「없음 통도 달라」를 받게 하면 된다 — 그때는 서버 판이다
 * ```
 */

/** 「캘린더를 안 고른 일정」 통의 id — 서버에 보내지 않는다. 화면 안에서만 쓴다. */
export const NO_CALENDAR = '__none__';

/** 그 일정이 속한 통 — `calendarId` 가 없으면 「없음」 통이다. */
export function bucketOf(ev: { calendarId?: string | null }): string {
  return ev.calendarId ?? NO_CALENDAR;
}

/**
 * 아무 통도 안 보이나 — 그러면 **서버를 부르지 않는다.**
 *
 * ⛔ `[]` 를 「전부」로 읽던 것이 결함 ⓑ 였다. 빈 고름은 «빈 결과»다.
 */
export function isNothingVisible(visible: readonly string[] | null): boolean {
  return visible !== null && visible.length === 0;
}

/**
 * 서버에 실어 보낼 `calendarIds`.
 *
 * ⛔ **「없음」 통이 보이는 동안에는 «안 보낸다»** — 보내면 서버의 `IN` 이 NULL 을 빼서
 *   그 통이 통째로 사라진다(결함 ⓐ). 그때는 전부 받아 와 [filterByVisible] 로 화면이 가른다.
 */
export function calendarQueryIds(visible: readonly string[] | null): string[] | undefined {
  if (visible === null || visible.length === 0) return undefined;
  if (visible.includes(NO_CALENDAR)) return undefined;   // ⛔ IN 이 NULL 을 뺀다
  return [...visible];
}

/** 받아온 일정을 보이는 통만 남긴다. `null`(전체)이면 그대로 둔다. */
export function filterByVisible<T extends { calendarId?: string | null }>(
  events: readonly T[],
  visible: readonly string[] | null,
): T[] {
  if (visible === null) return [...events];
  if (visible.length === 0) return [];
  return events.filter((e) => visible.includes(bucketOf(e)));
}
